import { useEffect, useMemo, useState } from "react";

import { ServiceRequest } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Table } from "@mantine/core";

import {
  HealthGorillaLabOrderInternalStatus,
  HealthGorillaLabOrderInternalStatusDisplay,
} from "@/health-gorilla/types/labs";
import {
  getLabOrderInternalStatusDisplay,
  getLabOrderReceivedDate,
} from "@/health-gorilla/utils/labs";
import { usDateFormat } from "@/utils/datetime";
import { getDisplayStringAsync } from "@/utils/medplum";

import LabOrderName from "./LabOrderName";

interface LabOrdersRowProps {
  result: ServiceRequest;
  onView: (labOrder: ServiceRequest) => void;
  showClientColumn: boolean;
}

export default function LabOrdersRow({ result, onView, showClientColumn }: LabOrdersRowProps) {
  const medplum = useMedplum();
  const [requestor, setRequestor] = useState<string>("");
  const [client, setClient] = useState<string>("");

  useEffect(() => {
    (async () => {
      const requestor = await getDisplayStringAsync(medplum, result.requester);
      setRequestor(requestor);
    })();
  }, [medplum, result.requester]);

  useEffect(() => {
    (async () => {
      const client = await getDisplayStringAsync(medplum, result.subject);
      setClient(client);
    })();
  }, [medplum, result.subject]);

  const status = useMemo(() => {
    return getLabOrderInternalStatusDisplay(result);
  }, [result]);

  const orderedDate = useMemo(() => {
    return usDateFormat(result.authoredOn) || "--";
  }, [result.authoredOn]);

  const receivedDate = useMemo(() => {
    const statuses: string[] = [
      HealthGorillaLabOrderInternalStatusDisplay[HealthGorillaLabOrderInternalStatus.Reviewed],
      HealthGorillaLabOrderInternalStatusDisplay[HealthGorillaLabOrderInternalStatus.NeedsReview],
    ];
    return statuses.includes(status) ? getLabOrderReceivedDate(result) || "--" : "--";
  }, [status, result]);

  const handleView = () => {
    onView(result);
  };

  const colSpans = useMemo(() => {
    if (showClientColumn) {
      return {
        client: 3,
        labs: 2,
        requestor: 3,
        orderedDate: 1,
        receivedDate: 1,
        status: 1,
        action: 1,
      };
    }
    return {
      client: 0,
      labs: 3,
      requestor: 2,
      orderedDate: 2,
      receivedDate: 2,
      status: 1,
      action: 2,
    };
  }, [showClientColumn]);

  return (
    <Table.Tr data-testid={result.id ? `lab-order-row-${result.id}` : "lab-order-row"}>
      {showClientColumn && <Table.Td colSpan={colSpans.client}>{client}</Table.Td>}
      <Table.Td colSpan={colSpans.labs} data-testid="lab-order-row-labs">
        <LabOrderName labOrder={result} />
      </Table.Td>
      <Table.Td colSpan={colSpans.requestor}>{requestor || "--"}</Table.Td>
      <Table.Td colSpan={colSpans.orderedDate} data-testid="lab-order-row-ordered-date">
        {orderedDate}
      </Table.Td>
      <Table.Td colSpan={colSpans.receivedDate} data-testid="lab-order-row-received-date">
        {receivedDate}
      </Table.Td>
      <Table.Td colSpan={colSpans.status} data-testid="lab-order-row-status">
        {status}
      </Table.Td>
      <Table.Td ta="right" colSpan={colSpans.action}>
        <Button variant="outline" onClick={handleView} data-testid="lab-order-row-view">
          View Details
        </Button>
      </Table.Td>
    </Table.Tr>
  );
}
